import * as React from "react";
import styled from "styled-components";
import { dollarFormatter } from "utils";
import { Status } from "components/status";
import { SubtleIconButton } from "components/button";
import { RiDownloadCloudFill } from "react-icons/ri";
import Placeholder from "../views/request/component/placeholder";
import { useHistory } from "react-router-dom";
import Table from "components/table";
import { useQuery } from "react-query";
import axios from "axios";
import { format, isValid, parseISO } from "date-fns";
import { TInvoice } from "types/invoice";
import { useTranslation } from 'react-i18next';
import { enUS, es } from "date-fns/locale";
import { useAuthContext } from "contexts/auth";
import convertCVD  from "../utils/convertCVD";

type Invoice = {
  id: number;
  stripe_id: string;
  fk_request_id: number;
  amount_due: number;
  hosted_invoice_url: string;
  invoice_pdf: string;
  status: Status;
} | null;

type Status = {
  label: string;
  color: string;
};

type Props = {
  id?: string;
  type?: "request" | "project" | "payment";
  data?: TInvoice[];
  setInvoiceTotalForProject?: (total: number) => void;
};

// this is used on the request page (/requests/[id]) and on the project page (/projects/[id]), and apparently on the payment page (/payments/[id]) but can't find logic to trigger it there
export default function InvoicesList({ ...props }: Props) {
  const { setInvoiceTotalForProject } = props
  const { t, i18n } = useTranslation();
  const tBase = "containers.invoicesList";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const history = useHistory();
  const { cvd_type } = useAuthContext();
  const locale = i18n.language === "es" ? es : enUS;

  const query = useQuery<{ invoices: TInvoice[] | null }>(
    ["invoices", props.id, props.type],
    async () =>
      props.data
        ? { invoices: props.data }
        : await axios
            .get(
              `
                ${process.env.REACT_APP_SERVER_URL}/api/v2/company/invoice?id=${props.id}&type=${props.type}
              `
            )
            .then((res) => res.data 
          )
  );

  const data = query.data?.invoices;

  if (setInvoiceTotalForProject && data) {
    let total = data.reduce((acc, curr:any) => {
      // if invoice is paid, add to total
      if (curr.fk_status_id !== 17) {
        return acc + curr.actual_total;
      }
    }, 0);

    setInvoiceTotalForProject(total);
  }

  const handleClick = (row: any) => {
    history.push(`/invoices/${row.id}`);
  };

  const columns = {
    id: {
      label: "ID",
      cell: (row: any) => <div className="font-mono">{row.id}</div>,
    },
    total: {
      label: "Total",
      cell: (row: any) => dollarFormatter(row.actual_total),
    },
    amount_due: {
      label: tr("Amount Due"),
      cell: (row: any) => {
        // Prefer computing from `actual_total - paid_total` because the list
        // endpoint may return an incorrect/stale `amount_due` for paid invoices.
        const actualTotal = Number(row?.actual_total);
        const paidTotal = Number(row?.paid_total);

        const hasComputedInputs =
          row?.actual_total !== undefined &&
          row?.paid_total !== undefined &&
          Number.isFinite(actualTotal) &&
          Number.isFinite(paidTotal);

        if (hasComputedInputs) {
          const computed = actualTotal - paidTotal; // cents
          return Number.isFinite(computed) ? dollarFormatter(computed) : null;
        }

        // Last resort: use direct field if present.
        const direct = row?.amount_due;
        if (direct !== undefined && direct !== null) return dollarFormatter(direct);

        // If API doesn't send amount_due / paid_total, but status is "Paid",
        // show $0.00 rather than blank.
        const statusLabel = String(row?.status?.label ?? "").toLowerCase();
        if (statusLabel === "paid") return dollarFormatter(0);

        return null;
      },
    },
    status: {
      label: tr("Status"),
      width: "100%",
      cell: (row: any) => {
        const statusColor = convertCVD(row.status.color, cvd_type);
        return <Status color={statusColor}>{tr(row.status.label)}</Status>
      },
    },
    creation_date: {
      label: tr("Created On"),
      cell: (row: any) => {
        try {
          const parsedDate = parseISO(row.creation_date);
          if (!isValid(parsedDate)) {
            throw new Error(`Invalid date: ${row.creation_date}`);
          }
          return format(parsedDate, "MMM dd, yyyy hh:mm aa", {
            locale: locale,
          });
        } catch (error) {
          console.error(`Error formatting date: ${row.creation_date}`, error);
          return "Invalid Date";
        }
      },
    },
    action: {
      label: "",
      cell: (row: any) => (
        <SubtleIconButton
          size="xxsmall"
          onClick={() => window.open(row.invoice_pdf, "_blank")}
        >
          <RiDownloadCloudFill />
        </SubtleIconButton>
      ),
    },
  };

  return (
    <div>
      {data && data.length > 0 && data[0] !== null ? (
        <Table columns={columns} data={data} handleClick={handleClick} />
      ) : (
        <Placeholder
          type="invoice"
          header={tr("No invoices yet.")}
          label={tr("Invoices will appear here when added.")}
        />
      )}
    </div>
  );
}

const Con = styled.div``;
